/**
 * ===========================================================================
 *  voice.js — synthesised human effort, pain and death
 * ===========================================================================
 * Source-filter vocal synthesis, the classic model: a glottal source (two
 * slightly detuned sawtooths plus a sub-octave triangle for chest) driven
 * through three resonant bandpass FORMANTS whose centre frequencies morph
 * along a vowel path. Breath noise sits underneath, a waveshaper adds rasp for
 * pain and death, and a plosive noise tick in front supplies the consonant.
 *
 * The result is stylised, never words — the point is that "hut!", "yaaah!" and
 * "guh-" are recognisable as a throat, and that 40 grunts fighting at once
 * sound like 40 different men because f0 is seeded from the entity id.
 * ===========================================================================
 */
import { env, envASR, gainNode, filter, noiseShot, shaperCurve, FLOOR } from './synth.js';

/* Formant triples (F1,F2,F3) in Hz for a male tract. */
const V = {
  a: [730, 1090, 2440],
  ae: [660, 1720, 2410],
  e: [530, 1840, 2480],
  i: [270, 2290, 3010],
  o: [570, 840, 2410],
  u: [325, 870, 2240],
};

/**
 * Vocal gestures. `path` is the vowel sequence, `pitch` the f0 multiplier
 * contour, both sampled evenly across the sound.
 */
export const GESTURE = {
  effort: { dur: 0.19, path: ['a', 'u'], pitch: [1.18, 0.98, 0.78], gain: 0.52, breath: 0.16, rasp: 0.18, plosive: 0.5, vib: 0 },
  shout: { dur: 0.62, path: ['ae', 'a', 'a'], pitch: [0.92, 1.30, 1.22, 1.02], gain: 0.60, breath: 0.22, rasp: 0.30, plosive: 0.3, vib: 26 },
  battlecry: { dur: 1.05, path: ['a', 'a', 'o'], pitch: [0.88, 1.22, 1.18, 0.92], gain: 0.55, breath: 0.26, rasp: 0.34, plosive: 0.2, vib: 34 },
  pain: { dur: 0.31, path: ['a', 'o'], pitch: [1.34, 0.86, 0.62], gain: 0.55, breath: 0.30, rasp: 0.62, plosive: 0.7, vib: 0 },
  death: { dur: 0.85, path: ['a', 'o', 'u'], pitch: [1.22, 0.94, 0.66, 0.46], gain: 0.58, breath: 0.40, rasp: 0.78, plosive: 0.5, vib: 12 },
  grunt: { dur: 0.16, path: ['o', 'u'], pitch: [1.0, 0.82], gain: 0.32, breath: 0.14, rasp: 0.30, plosive: 0.4, vib: 0 },
  cheer: { dur: 0.75, path: ['a', 'e'], pitch: [0.95, 1.35, 1.25], gain: 0.5, breath: 0.2, rasp: 0.2, plosive: 0.2, vib: 30 },
};

const lerpArr = (arr, u) => {
  const n = arr.length - 1, i = Math.min(n - 1, Math.floor(u * n)), f = u * n - i;
  return arr[i] + (arr[i + 1] - arr[i]) * f;
};

/** Deterministic per-unit voice pitch. Same entity -> same throat, always. */
export function voicePitch(seed, kind = 1) {
  let h = (seed | 0) * 2654435761 >>> 0;
  h ^= h >>> 13; h = (h * 1274126177) >>> 0;
  const u = (h >>> 8) / 16777216;
  // officers/generals sit lower and broader than peons
  const base = kind >= 3 ? 88 : 104;
  const span = kind >= 3 ? 26 : 52;
  return base + u * span;
}

/**
 * Build one vocalisation into `A.out`.
 * p: { type, f0, gain, rate }
 */
export function voiceShot(A, t, p) {
  const ctx = A.ctx, rnd = A.rnd;
  const G = GESTURE[p.type] || GESTURE.effort;
  const dur = G.dur * (p.rate ? 1 / p.rate : 1) * (0.86 + rnd() * 0.30);
  const f0 = (p.f0 || 120) * (0.97 + rnd() * 0.06);
  const amp = G.gain * (p.gain ?? 1);

  // --- output stage: throat lowpass -> rasp -> envelope --------------------
  const outG = gainNode(ctx, 0);
  const shaper = ctx.createWaveShaper();
  shaper.curve = shaperCurve(1 + G.rasp * 6);
  const throat = filter(ctx, 'lowpass', 3000 + rnd() * 900, 0.9);
  const chest = filter(ctx, 'lowshelf', 220, 0);
  chest.gain.value = 4;
  throat.connect(shaper); shaper.connect(chest); chest.connect(outG); outG.connect(A.out);
  // an asymmetric envelope: fast in, slower out — that is what makes it a shout
  const atk = Math.max(0.006, dur * (p.type === 'shout' || p.type === 'battlecry' ? 0.10 : 0.05));
  envASR(outG.gain, t, amp, atk, dur * 0.35, dur * 0.62);

  // --- formants ------------------------------------------------------------
  const mix = gainNode(ctx, 1);
  mix.connect(throat);
  const fg = [1.0, 0.52, 0.26];
  const bw = [92, 118, 168];
  const steps = 5;
  for (let k = 0; k < 3; k++) {
    const bp = filter(ctx, 'bandpass', V[G.path[0]][k], V[G.path[0]][k] / bw[k]);
    bp.Q.value = Math.max(1.6, V[G.path[0]][k] / bw[k]);
    // morph the vowel across the gesture
    bp.frequency.setValueAtTime(V[G.path[0]][k], t);
    for (let s = 1; s <= steps; s++) {
      const u = s / steps;
      const seg = Math.min(G.path.length - 1, Math.floor(u * (G.path.length - 1) + 0.0001));
      const nx = Math.min(G.path.length - 1, seg + 1);
      const lf = (u * (G.path.length - 1)) - seg;
      const f = V[G.path[seg]][k] + (V[G.path[nx]][k] - V[G.path[seg]][k]) * lf;
      bp.frequency.linearRampToValueAtTime(f * (0.94 + rnd() * 0.12), t + dur * u);
    }
    const g = gainNode(ctx, fg[k]);
    bp.connect(g); g.connect(mix);
    A._formants[k] = bp;
  }

  // --- glottal source ------------------------------------------------------
  const src = gainNode(ctx, 0.5);
  src.connect(A._formants[0]); src.connect(A._formants[1]); src.connect(A._formants[2]);
  const vibLFO = G.vib > 0 ? ctx.createOscillator() : null;
  let vibG = null;
  if (vibLFO) {
    vibLFO.type = 'sine'; vibLFO.frequency.setValueAtTime(4.6 + rnd() * 2.4, t);
    vibG = gainNode(ctx, 0);
    envASR(vibG.gain, t, G.vib, dur * 0.35, dur * 0.3, dur * 0.4);
    vibLFO.connect(vibG); vibLFO.start(t); vibLFO.stop(t + dur + 0.2);
  }
  for (let i = 0; i < 3; i++) {
    const o = ctx.createOscillator();
    o.type = i === 2 ? 'triangle' : 'sawtooth';
    const mult = i === 2 ? 0.5 : 1;
    const det = i === 1 ? (7 + rnd() * 9) : 0;
    o.frequency.setValueAtTime(f0 * mult * lerpArr(G.pitch, 0), t);
    for (let s = 1; s <= 6; s++) {
      const u = s / 6;
      o.frequency.linearRampToValueAtTime(Math.max(30, f0 * mult * lerpArr(G.pitch, u)), t + dur * u);
    }
    if (det) o.detune.setValueAtTime(det, t);
    if (vibG) vibG.connect(o.detune);
    const og = gainNode(ctx, i === 2 ? 0.42 : (i === 0 ? 0.6 : 0.35));
    o.connect(og); og.connect(src);
    o.start(t); o.stop(t + dur + 0.25);
  }

  // --- breath + consonant --------------------------------------------------
  if (G.breath > 0) {
    const hp = filter(ctx, 'bandpass', 1500 + rnd() * 900, 0.8);
    const bg = gainNode(ctx, 0);
    envASR(bg.gain, t, G.breath * amp * 0.9, atk * 0.6, dur * 0.25, dur * 0.7);
    const s = noiseShot(ctx, A.n.white, t, dur + 0.2, rnd);
    s.connect(hp); hp.connect(bg); bg.connect(throat);
    s.stop(t + dur + 0.25);
  }
  if (G.plosive > 0) {
    const lp = filter(ctx, 'lowpass', 1400 + rnd() * 900, 0.9);
    const pg = gainNode(ctx, 0);
    env(pg.gain, t, G.plosive * amp * 0.55, 0.001, 0.035);
    const s = noiseShot(ctx, A.n.white, t, 0.06, rnd);
    s.connect(lp); lp.connect(pg); pg.connect(throat);
    s.stop(t + 0.08);
  }
  return dur + 0.3;
}

/** Registered like an SFX id so `play('voice_pain', …)` just works. */
export const VOICE_IDS = ['voice_effort', 'voice_shout', 'voice_pain', 'voice_death',
  'voice_battlecry', 'voice_grunt', 'voice_cheer'];
